import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Fade,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Theme,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormikProps } from 'formik';
import React from 'react';
import { FormikSwitch, FormikTextField } from '../../common/formik-material-ui';
import { CustomCrmInputs, ICustomCRM } from '../subpages/CustomCrmInputs';

export const InfoTooltip = (props: Omit<TooltipProps, 'children'>) => {
  return (
    <Tooltip
      style={{ cursor: 'pointer' }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      placement="right-start"
      {...props}
    >
      <InfoIcon />
    </Tooltip>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  button: {
    height: '20px',
    marginRight: theme.spacing(1),
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  headerColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function CustomCRM(props: {
  formik: FormikProps<any>;
  setValidationOfCustomCRM: React.Dispatch<
    React.SetStateAction<ICustomCRM | undefined>
  >;
}) {
  const classes = useStyles();
  return (
    <div className="container">
      <div className="row">
        <div className="col-lg-6">
          <FormikTextField
            data-qa="custom-crm-name"
            name="customCRM.crmName"
            fullWidth
            // required
            className={classes.field}
            formik={props.formik}
            label="CRM Name"
            size="small"
            InputProps={{
              inputProps: {
                maxLength: 150,
              },
              readOnly: !props.formik.values.customCRM.hasCustomCRM,
            }}
            variant="outlined"
          />
        </div>
        <div className="col-lg-6">
          <div
            className="row"
            style={{
              paddingLeft: '101px',
              paddingTop: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                <FormikSwitch
                  name="customCRM.hasCustomCRM"
                  data-qa="use-custom-crm-toggle"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Use Custom CRM"
            />
            <InfoTooltip title="Enable Custom CRM" />
          </div>
          <div
            className="row"
            style={{
              paddingLeft: '101px',
              paddingTop: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                <FormikSwitch
                  name="customCRM.applyCustomCRMtoNewUsers"
                  data-qa="apply-to-all-users-toggle"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Apply To All Users"
            />
            <InfoTooltip title="When “Apply to all users” is toggled ON then all users will have the CRM turned on when added to the system. When it is OFF then new users will not have the CRM added." />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <CustomCrmInputs
            formik={props.formik}
            setValidationOfCustomCRM={props.setValidationOfCustomCRM}
          />
        </div>
      </div>
      <div className="row">
        {props.formik.values.customCRM.headers.map((header, index) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                paddingLeft: '12.5px',
              }}
              key={index}
            >
              <div
                className={classes.headerColumn}
                style={{ width: '42%', paddingRight: '8px' }}
              >
                <FormikTextField
                  name={`customCRM.headers.${index}.name`}
                  className={classes.field}
                  formik={props.formik}
                  fullWidth
                  label="Header Name"
                  InputProps={{
                    inputProps: {
                      maxLength: 150,
                    },
                    readOnly: !props.formik.values.customCRM.hasCustomCRM,
                  }}
                  size="small"
                  variant="outlined"
                />
              </div>
              <div className={classes.headerColumn} style={{ width: '42%' }}>
                <FormikTextField
                  name={`customCRM.headers.${index}.value`}
                  className={classes.field}
                  formik={props.formik}
                  fullWidth
                  InputProps={{
                    inputProps: {
                      maxLength: 150,
                    },
                    readOnly: !props.formik.values.customCRM.hasCustomCRM,
                    endAdornment: (
                      <InputAdornment position="end">
                        <InfoTooltip title="Name and Value fields will apply to all the enpoints entered above" />
                      </InputAdornment>
                    ),
                  }}
                  label="Header Value"
                  size="small"
                  variant="outlined"
                />
              </div>
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  flexWrap: 'nowrap',
                  width: '16%',
                  paddingLeft: '8px',
                }}
              >
                <Tooltip title="add">
                  <>
                    <IconButton
                      aria-label="add"
                      color="primary"
                      onClick={() => {
                        props.formik.setFieldValue('customCRM.headers', [
                          ...props.formik.values.customCRM.headers.slice(
                            0,
                            index + 1
                          ),
                          {
                            name: '',
                            value: '',
                          },
                          ...props.formik.values.customCRM.headers.slice(
                            index + 1
                          ),
                        ]);
                      }}
                      disabled={
                        props.formik.values.customCRM.headers.length === 10 ||
                        !props.formik.values.customCRM.hasCustomCRM
                      }
                      size="large"
                    >
                      <PlaylistAddIcon />
                    </IconButton>
                  </>
                </Tooltip>
                <Tooltip key={index} title="remove">
                  <>
                    <IconButton
                      className={classes.deleteIconButton}
                      aria-label="cancel"
                      onClick={() => {
                        props.formik.setFieldValue('customCRM.headers', [
                          ...props.formik.values.customCRM.headers.slice(
                            0,
                            index
                          ),
                          ...props.formik.values.customCRM.headers.slice(
                            index + 1
                          ),
                        ]);
                      }}
                      disabled={
                        props.formik.values.customCRM.headers.length <= 1 ||
                        !props.formik.values.customCRM.hasCustomCRM
                      }
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
